import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  CheckCircle,
  CircleHelp,
  Clock3,
  Eye,
  FileBadge,
  Hash,
  LoaderCircle,
  Play,
  RotateCcw,
  StopCircle,
  X,
  XCircle,
} from "lucide-react";

import NoIndex from "../../../components/NoIndex";
import Pagination from "../../../utils/TablePagination";

import useCampaigns from "../../../services/Whatsapp/useCampaigns";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import LoadingScreen from "../../../utils/LoadingScreen";

const getCampaignStatusConfig = (status) => {
  const normalizedStatus = String(status || "")
    .trim()
    .toLowerCase();

  const statusMap = {
    started: {
      label: "Started",
      icon: Clock3,
      className: "bg-blue-100 text-blue-700 border-blue-200",
      iconClassName: "text-blue-600",
    },
    running: {
      label: "Running",
      icon: LoaderCircle,
      className: "bg-amber-100 text-amber-700 border-amber-200",
      iconClassName: "text-amber-600 animate-spin",
    },
    completed: {
      label: "Completed",
      icon: CheckCircle,
      className: "bg-green-100 text-green-700 border-green-200",
      iconClassName: "text-green-600",
    },
    failed: {
      label: "Failed",
      icon: XCircle,
      className: "bg-red-100 text-red-700 border-red-200",
      iconClassName: "text-red-600",
    },

    // Raw backend values fallback
    y: {
      label: "Running",
      icon: LoaderCircle,
      className: "bg-amber-100 text-amber-700 border-amber-200",
      iconClassName: "text-amber-600 animate-spin",
    },
    n: {
      label: "Completed",
      icon: CheckCircle,
      className: "bg-green-100 text-green-700 border-green-200",
      iconClassName: "text-green-600",
    },
    f: {
      label: "Failed",
      icon: XCircle,
      className: "bg-red-100 text-red-700 border-red-200",
      iconClassName: "text-red-600",
    },
  };

  return (
    statusMap[normalizedStatus] || {
      label: status || "Unknown",
      icon: CircleHelp,
      className: "bg-gray-100 text-gray-700 border-gray-200",
      iconClassName: "text-gray-500",
    }
  );
};

const CampaignTable = () => {
  const navigate = useNavigate();

  const { loading, getCampaigns } = useCampaigns();
  const { templates, fetchVendorTemplates } = useTemplates();
  const { whatsappNumbers } = useWhatsappNumbers();

  const [singleWhatsApp, setSingleWhatsApp] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const itemsPerPage = 10;

  const loadCampaigns = async () => {
    setTableLoading(true);
    const data = await getCampaigns(singleWhatsApp);
    setFilteredCampaigns(Array.isArray(data) ? data : []);
    setTableLoading(false);
  };

  const handleRefresh = async () => {
    if (!singleWhatsApp) return;
    const start = Date.now();
    setTableLoading(true);
    const data = await getCampaigns(singleWhatsApp);
    setFilteredCampaigns(Array.isArray(data) ? data : []);
    setTableLoading(false);
    const elapsed = Date.now() - start;
    if (elapsed < 5000) {
      await new Promise((r) => setTimeout(r, 5000 - elapsed));
    }
  };

  const getStatusStyle = (status) => {
    const s = status?.toLowerCase();

    if (s === "completed") {
      return "bg-green-600 text-white";
    }
    if (s === "failed") {
      return "bg-red-600 text-white";
    }
    if (s === "pending") {
      return "bg-yellow-500 text-white";
    }
    return "bg-gray-400 text-white";
  };

  useEffect(() => {
    if (!singleWhatsApp) return;

    const loadCampaigns = async () => {
      const data = await getCampaigns(singleWhatsApp);
      setFilteredCampaigns(Array.isArray(data) ? data : []);
    };

    loadCampaigns();
  }, [singleWhatsApp]);

  const sortedData = [...filteredCampaigns].sort(
    (a, b) => new Date(b.campaign_start || 0) - new Date(a.campaign_start || 0),
  );

  const filteredData = sortedData.filter((item) =>
    item.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePreview = (templateId) => {
    const template = templates.find(
      (t) =>
        t.name?.toLowerCase() === templateId?.toLowerCase() ||
        t.template_name?.toLowerCase() === templateId?.toLowerCase() ||
        t.templateId?.toLowerCase() === templateId?.toLowerCase(),
    );

    if (!template) {
      toast.error("Template not found");
      return;
    }

    setPreviewTemplate(template);
  };

  const decodeBase64 = (str) => {
    try {
      return atob(str);
    } catch {
      return str;
    }
  };

  const getTemplateParts = (template) => {
    if (!template) return {};
    const components = template.components || [];
    const header = components.find((c) => c.type === "HEADER");
    const body = components.find((c) => c.type === "BODY");
    const footer = components.find((c) => c.type === "FOOTER");
    const buttons = components.find((c) => c.type === "BUTTONS");
    return {
      header: header?.text ? decodeBase64(header.text) : null,
      headerFormat: header?.format || null,
      body: body?.text ? decodeBase64(body.text) : null,
      footer: footer?.text ? decodeBase64(footer.text) : null,
      buttons: buttons?.buttons || [],
    };
  };

  const timeStr = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  useEffect(() => {
    if (whatsappNumbers.length > 0 && singleWhatsApp === "") {
      setSingleWhatsApp(whatsappNumbers[0].phone_no_id);
    }
  }, [whatsappNumbers]);

  useEffect(() => {
    if (singleWhatsApp) {
      fetchVendorTemplates(singleWhatsApp);
    }
  }, [singleWhatsApp, fetchVendorTemplates]);

  return (
    <>
      <NoIndex />
      <ToastContainer />

      <div className=" min-h-full p-6 montserrat-font-1">
        {/* Top Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 montserrat-font-1">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Manage Campaign</h2>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  navigate("/WhatsApp/NewCampaign", {
                    // state: { startStep: 1 },
                  })
                }
                className="bg-green-600 hover:bg-green-700 text-white montserrat-font-1 px-5 py-2 rounded-md text-sm font-medium"
              >
                + Create New Campaign
              </button>

              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 border rounded-md px-3 py-2 bg-blue-500 text-white"
              >
                <RotateCcw strokeWidth={2.5} size={16} />
                Refresh
              </button>
            </div>
          </div>

          <hr className="mb-4" />

          <div className="flex flex-col md:flex-row md:items-end gap-6 montserrat-font-1">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                WhatsApp Number
              </label>
              <select
                value={singleWhatsApp}
                onChange={(e) => setSingleWhatsApp(e.target.value)}
                className="w-full md:w-72 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select Number</option>
                {whatsappNumbers.map((num, index) => (
                  <option key={index} value={num.phone_no_id}>
                    {num.phone_no_id} - ({num.phone_no})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col montserrat-font-1">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Search campaigns
              </label>
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-72 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border montserrat-font-1">
          {tableLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center montserrat-font-1 rounded-lg">
              <LoadingScreen text="Loading Campaigns..." />
            </div>
          )}
          <div className="overflow-x-auto montserrat-font-1">
            <table className="min-w-full text-sm text-center border-collapse">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="border border-gray-200 px-4 py-3">S.No.</th>
                  <th className="border border-gray-200">Campaign Name</th>
                  <th className="border border-gray-200">Template/Category</th>
                  <th className="border border-gray-200">Start/End Time</th>
                  <th className="border border-gray-200">Status</th>
                  <th className="border border-gray-200">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-6 border border-gray-200">
                      Loading...
                    </td>
                  </tr>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="py-6 border border-gray-200 text-gray-500"
                    >
                      No campaigns found
                    </td>
                  </tr>
                ) : (
                  Array.isArray(currentItems) &&
                  currentItems.map((item, index) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="border border-gray-200 py-3">
                        {index + startIndex + 1}
                      </td>

                      <td className="border border-gray-200 px-3 py-2">
                        <div className="flex flex-col items-center gap-1">
                          {/* Campaign Name + Excel */}
                          {item.excel_url &&
                          item.excel_url !== "null" &&
                          item.excel_url.trim() !== "" ? (
                            <a
                              href={item.excel_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-1 text-blue-600 cursor-pointer hover:underline"
                              title="Download Excel"
                            >
                              <FileBadge className="w-4 h-4" />
                              <span className="text-sm font-medium">
                                {item.campaign_name}
                              </span>
                            </a>
                          ) : (
                            <span className="text-sm font-medium text-gray-800">
                              {item.campaign_name}
                            </span>
                          )}

                          {/* ID Badge */}
                          <span className="inline-flex items-center gap-1 w-fit bg-green-100 text-gray-700 px-2 py-1 rounded-md text-xs font-medium">
                            <Hash className="w-3 h-3" />
                            {item.id}
                          </span>

                          <span
                            className={`inline-flex items-center gap-1 w-fit px-2 py-1 rounded-md text-xs font-medium ${
                              item.campaignType === "Scheduled"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {item.campaignType || "N/A"}
                          </span>
                        </div>
                      </td>

                      <td className="px-5 py-3 text-center border border-gray-200 montserrat-font-1">
                        <div className="flex flex-col items-center gap-1">
                          {/* Template → Preview */}
                          <div
                            onClick={() => handlePreview(item.template_id)}
                            className="text-sm font-semibold flex gap-2 text-blue-600 hover:underline cursor-pointer"
                            title="Preview Template"
                          >
                            <Eye className="w-4 h-4" />
                            {item.template_id}
                          </div>

                          {/* Category */}
                          <span className="text-sm text-gray-800">
                            {item.template_category}
                          </span>
                        </div>
                      </td>

                      <td className="border border-gray-200 px-3 py-2">
                        <div className="flex flex-col gap-1">
                          {/* Start Date */}
                          <div className="flex items-center gap-2">
                            <Play className="w-3 h-3 text-green-600" />
                            <span className="text-sm font-medium text-gray-800">
                              {item.campaign_start || "N/A"}
                            </span>
                          </div>

                          {/* End Date */}
                          <div className="flex items-center gap-2">
                            <StopCircle className="w-3 h-3 text-red-500" />
                            <span className="text-xs text-gray-500">
                              {item.campaign_end || "N/A"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="border border-gray-200">
                        <span
                          className={`${getStatusStyle(item.status)} text-xs px-4 py-1 rounded-full flex items-center justify-center gap-1 mx-auto w-fit`}
                        >
                          {item.status === "Completed" && (
                            <CheckCircle size={14} />
                          )}
                          {item.status === "Failed" && <X size={14} />}
                          {item.status === "Pending" && <RotateCcw size={14} />}
                          {item.status || "N/A"}
                        </span>
                      </td>

                      <td className="border border-gray-200">
                        <button
                          onClick={() =>
                            navigate(
                              `/WhatsApp/Manage/CampaignView?campaignId=${item.id}&phoneId=${singleWhatsApp}`,
                            )
                          }
                        >
                          <Eye
                            className="w-5 h-5 mx-auto opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                            strokeWidth={2}
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onNextPage={() => setCurrentPage((p) => p + 1)}
            onPrevPage={() => setCurrentPage((p) => p - 1)}
            onPageClick={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* ── Message Preview Modal ── */}
      {previewTemplate &&
        (() => {
          const { header, headerFormat, body, footer, buttons } =
            getTemplateParts(previewTemplate);
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center montserrat-font-1 bg-black/40"
              onClick={() => setPreviewTemplate(null)}
            >
              <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex justify-between items-center px-5 py-4 border-b">
                  <h3 className="text-base font-semibold text-gray-800">
                    Message Preview
                  </h3>
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="px-5 py-5 montserrat-font-1">
                  {/* WhatsApp style bubble */}
                  <div className="bg-gray-100 rounded-xl p-4 mb-3">
                    {/* Header text */}
                    {header && headerFormat === "TEXT" && (
                      <p className="font-semibold text-gray-800 text-sm mb-2">
                        {header}
                      </p>
                    )}

                    {/* Header image */}
                    {header && headerFormat === "IMAGE" && (
                      <img
                        src={header}
                        alt="header"
                        className="w-full rounded-lg mb-2 object-cover max-h-40"
                      />
                    )}

                    {/* Body */}
                    {body && (
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">
                        {body}
                      </p>
                    )}

                    {/* Footer + Time */}
                    <div className="flex justify-between items-end mt-3">
                      {footer && (
                        <p className="text-gray-400 text-xs">{footer}</p>
                      )}
                      <p className="text-gray-400 text-xs ml-auto">{timeStr}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {buttons.length > 0 && (
                    <div className="space-y-2">
                      {buttons.map((btn, i) => (
                        <div
                          key={i}
                          className="border border-gray-200 rounded-lg py-2 text-center text-sm font-medium text-green-600"
                        >
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Close Button */}
                <div className="flex justify-end px-5 pb-5 montserrat-font-1">
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="bg-gray-700 hover:bg-gray-800 text-white px-5 py-2 rounded-md text-sm font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </>
  );
};

export default CampaignTable;
